import {
  DASHBOARD_COLUMNS,
  DASHBOARD_GAP,
  DASHBOARD_MAX_PREFERRED_ROW,
  DASHBOARD_ROW_UNIT,
  DASHBOARD_SECTION_IDS,
} from '~/config/dashboard/types';
import type {
  DashboardLayoutConfig,
  DashboardModuleDefinition,
  DashboardModuleKey,
  DashboardRuntimePlacement,
  DashboardSectionLayout,
  DashboardWidgetMeasurement,
  DashboardWidgetPlacement,
} from '~/config/dashboard/types';

const measurementTolerance = 1.5;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const clamp = (value: number, minimum: number, maximum: number) => Math.min(Math.max(value, minimum), maximum);

const finiteInteger = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback;

const isDashboardSectionId = (value: unknown): value is DashboardWidgetPlacement['sectionId'] =>
  typeof value === 'string' && DASHBOARD_SECTION_IDS.some((sectionId) => sectionId === value);

const definitionMap = (definitions: DashboardModuleDefinition[]) =>
  new Map<DashboardModuleKey, DashboardModuleDefinition>(definitions.map((definition) => [definition.key, definition]));

export const createDefaultDashboardLayout = (definitions: DashboardModuleDefinition[]): DashboardLayoutConfig => ({
  placements: definitions.map((definition) => ({ ...definition.defaultPlacement })),
  disabledModuleKeys: [],
});

export const updateDashboardModuleSelection = (
  layout: DashboardLayoutConfig,
  definitionKeys: DashboardModuleKey[],
  enabledKeys: DashboardModuleKey[],
  scopeKeys: DashboardModuleKey[]
): DashboardLayoutConfig => {
  const enabled = new Set(enabledKeys);
  const scope = new Set(scopeKeys);
  return {
    ...layout,
    disabledModuleKeys: definitionKeys.filter((key) => (scope.has(key) ? !enabled.has(key) : layout.disabledModuleKeys.includes(key))),
  };
};

export const isDefaultDashboardLayout = (layout: DashboardLayoutConfig, definitions: DashboardModuleDefinition[]) => {
  if (layout.disabledModuleKeys.length > 0 || layout.placements.length !== definitions.length) return false;
  const placements = new Map(layout.placements.map((placement) => [placement.key, placement]));
  return definitions.every((definition) => {
    const current = placements.get(definition.key);
    const expected = definition.defaultPlacement;
    return (
      current?.sectionId === expected.sectionId &&
      current.x === expected.x &&
      current.width === expected.width &&
      current.preferredRow === expected.preferredRow &&
      current.fillEmptySpace === expected.fillEmptySpace &&
      current.layoutOrder === expected.layoutOrder &&
      current.mobileOrder === expected.mobileOrder
    );
  });
};

export const sanitizeDashboardLayout = (value: unknown, definitions: DashboardModuleDefinition[]): DashboardLayoutConfig | undefined => {
  if (!isRecord(value) || !Array.isArray(value.placements) || !Array.isArray(value.disabledModuleKeys)) return undefined;

  const definitionsByKey = definitionMap(definitions);
  const seen = new Set<DashboardModuleKey>();
  const placements: DashboardWidgetPlacement[] = [];

  value.placements.forEach((candidate) => {
    if (!isRecord(candidate) || typeof candidate.key !== 'string') return;
    const definition = definitionsByKey.get(candidate.key as DashboardModuleKey);
    if (!definition || seen.has(definition.key)) return;
    const fallback = definition.defaultPlacement;
    const width = clamp(finiteInteger(candidate.width, fallback.width), definition.minWidth, definition.maxWidth);
    const x = clamp(finiteInteger(candidate.x, fallback.x), 0, DASHBOARD_COLUMNS - width);
    const sectionId = isDashboardSectionId(candidate.sectionId) ? candidate.sectionId : fallback.sectionId;
    placements.push({
      key: definition.key,
      sectionId,
      x,
      width,
      preferredRow: clamp(finiteInteger(candidate.preferredRow, fallback.preferredRow), 0, DASHBOARD_MAX_PREFERRED_ROW),
      fillEmptySpace: typeof candidate.fillEmptySpace === 'boolean' ? candidate.fillEmptySpace : false,
      layoutOrder: Math.max(0, finiteInteger(candidate.layoutOrder, fallback.layoutOrder)),
      mobileOrder: Math.max(0, finiteInteger(candidate.mobileOrder, fallback.mobileOrder)),
    });
    seen.add(definition.key);
  });

  const disabled = new Set<DashboardModuleKey>();
  value.disabledModuleKeys.forEach((key) => {
    if (typeof key === 'string' && definitionsByKey.has(key as DashboardModuleKey)) disabled.add(key as DashboardModuleKey);
  });

  definitions.forEach((definition) => {
    if (seen.has(definition.key)) return;
    placements.push({ ...definition.defaultPlacement });
    if (!definition.mandatory) disabled.add(definition.key);
  });

  return { placements, disabledModuleKeys: [...disabled] };
};

export const migrateDashboardLayoutV6 = (value: unknown, definitions: DashboardModuleDefinition[]): DashboardLayoutConfig | undefined => {
  const migrated = sanitizeDashboardLayout(value, definitions);
  if (!migrated) return undefined;
  const defaultsByKey = new Map(definitions.map((definition) => [definition.key, definition.defaultPlacement]));
  const defaultFillLayout: DashboardLayoutConfig = {
    ...migrated,
    placements: migrated.placements.map((placement) => ({
      ...placement,
      fillEmptySpace: defaultsByKey.get(placement.key)?.fillEmptySpace ?? false,
    })),
  };
  return isDefaultDashboardLayout(defaultFillLayout, definitions) ? createDefaultDashboardLayout(definitions) : migrated;
};

const legacyWidth = (value: unknown, fallback: number) => {
  if (typeof value !== 'string') return fallback;
  if (value.includes('col-span-full')) return DASHBOARD_COLUMNS;
  const match = value.match(/col-span-(\d+)/);
  return match ? Number.parseInt(match[1], 10) : fallback;
};

export const migrateLegacyDashboardLayout = (
  value: unknown,
  definitions: DashboardModuleDefinition[]
): DashboardLayoutConfig | undefined => {
  if (!Array.isArray(value)) return undefined;
  const definitionsByKey = definitionMap(definitions);
  const selected: DashboardModuleDefinition[] = [];
  const legacyByKey = new Map<DashboardModuleKey, Record<string, unknown>>();

  value.forEach((candidate) => {
    if (!isRecord(candidate) || typeof candidate.key !== 'string') return;
    const definition = definitionsByKey.get(candidate.key as DashboardModuleKey);
    if (!definition || legacyByKey.has(definition.key)) return;
    selected.push(definition);
    legacyByKey.set(definition.key, candidate);
  });

  if (selected.length === 0) return undefined;

  const sectionCursor = new Map<DashboardWidgetPlacement['sectionId'], number>();
  const sectionOrder = new Map<DashboardWidgetPlacement['sectionId'], number>();
  const migrated = new Map<DashboardModuleKey, DashboardWidgetPlacement>();

  selected.forEach((definition) => {
    const fallback = definition.defaultPlacement;
    const width = clamp(legacyWidth(legacyByKey.get(definition.key)?.width, fallback.width), definition.minWidth, definition.maxWidth);
    let x = sectionCursor.get(fallback.sectionId) ?? 0;
    if (x + width > DASHBOARD_COLUMNS) x = 0;
    const layoutOrder = sectionOrder.get(fallback.sectionId) ?? 0;
    migrated.set(definition.key, { ...fallback, x, width, fillEmptySpace: false, layoutOrder });
    const nextX = x + width;
    sectionCursor.set(fallback.sectionId, nextX >= DASHBOARD_COLUMNS ? 0 : nextX);
    sectionOrder.set(fallback.sectionId, layoutOrder + 1);
  });

  const selectedKeys = new Set(selected.map((definition) => definition.key));
  return {
    placements: definitions.map((definition) => migrated.get(definition.key) ?? { ...definition.defaultPlacement, fillEmptySpace: false }),
    disabledModuleKeys: definitions.filter((definition) => !selectedKeys.has(definition.key)).map((definition) => definition.key),
  };
};

const horizontallyOverlaps = (a: DashboardWidgetPlacement, b: DashboardWidgetPlacement) => a.x < b.x + b.width && a.x + a.width > b.x;

const verticallyConflicts = (top: number, height: number, placed: DashboardRuntimePlacement, gap: number) =>
  top < placed.top + placed.height + gap && top + height + gap > placed.top;

export const solveDashboardSection = ({
  placements,
  measurements,
  containerWidth,
  gap = DASHBOARD_GAP,
  rowUnit = DASHBOARD_ROW_UNIT,
}: {
  placements: DashboardWidgetPlacement[];
  measurements: Map<DashboardModuleKey, DashboardWidgetMeasurement>;
  containerWidth: number;
  gap?: number;
  rowUnit?: number;
}): DashboardSectionLayout => {
  if (placements.length === 0) return { placements: [], height: 0, ready: true };
  if (containerWidth <= 0) return { placements: [], height: 0, ready: false };

  const columnWidth = (containerWidth - gap * (DASHBOARD_COLUMNS - 1)) / DASHBOARD_COLUMNS;
  const sorted = [...placements].sort(
    (a, b) => a.layoutOrder - b.layoutOrder || a.preferredRow - b.preferredRow || a.key.localeCompare(b.key)
  );
  const runtime: DashboardRuntimePlacement[] = [];
  const placementByKey = new Map(sorted.map((placement) => [placement.key, placement]));
  let ready = true;

  sorted.forEach((placement) => {
    const pixelWidth = placement.width * columnWidth + (placement.width - 1) * gap;
    const measurement = measurements.get(placement.key);
    const measurementMatches = measurement && Math.abs(measurement.measuredWidth - pixelWidth) <= measurementTolerance;
    if (!measurementMatches) ready = false;
    const height = measurement ? Math.max(0, measurement.measuredHeight) : 0;
    let top = placement.preferredRow * rowUnit;

    while (true) {
      const blockers = runtime.filter((placed) => {
        const blockerPlacement = placementByKey.get(placed.key);
        return blockerPlacement && horizontallyOverlaps(placement, blockerPlacement) && verticallyConflicts(top, height, placed, gap);
      });
      if (blockers.length === 0) break;
      top = Math.max(...blockers.map((blocker) => blocker.top + blocker.height + gap));
    }

    runtime.push({
      key: placement.key,
      left: placement.x * (columnWidth + gap),
      top,
      width: pixelWidth,
      height,
    });
  });

  if (ready && sorted.some((placement) => placement.fillEmptySpace)) {
    const terminalByColumn = new Array<DashboardRuntimePlacement | undefined>(DASHBOARD_COLUMNS).fill(undefined);
    runtime.forEach((placement) => {
      const source = placementByKey.get(placement.key);
      if (!source) return;
      const placementBottom = placement.top + placement.height;
      for (let column = source.x; column < source.x + source.width; column += 1) {
        const terminal = terminalByColumn[column];
        if (!terminal || placementBottom > terminal.top + terminal.height) terminalByColumn[column] = placement;
      }
    });
    const bottom = Math.max(...runtime.map((placement) => placement.top + placement.height));
    runtime.forEach((placement) => {
      const source = placementByKey.get(placement.key);
      if (!source?.fillEmptySpace) return;
      const isTerminal = Array.from({ length: source.width }, (_, index) => source.x + index).every(
        (column) => terminalByColumn[column]?.key === placement.key
      );
      if (!isTerminal) return;
      const extraHeight = bottom - placement.top - placement.height;
      if (extraHeight <= 0) return;
      placement.height += extraHeight;
      placement.fillHeight = placement.height;
    });
  }

  return {
    placements: runtime,
    height: runtime.length ? Math.max(...runtime.map((placement) => placement.top + placement.height)) : 0,
    ready,
  };
};

export const anchoredDashboardX = (placement: DashboardWidgetPlacement, width: number) => {
  if (placement.x === 0) return 0;
  if (placement.x + placement.width === DASHBOARD_COLUMNS) return DASHBOARD_COLUMNS - width;
  return clamp(placement.x, 0, DASHBOARD_COLUMNS - width);
};

export const resizeDashboardPlacementFromEdge = (
  placement: DashboardWidgetPlacement,
  edge: 'left' | 'right',
  columnDelta: number,
  minimumWidth: number,
  maximumWidth: number
): Pick<DashboardWidgetPlacement, 'x' | 'width'> => {
  if (edge === 'right') {
    const maximum = Math.min(maximumWidth, DASHBOARD_COLUMNS - placement.x);
    return {
      x: placement.x,
      width: clamp(placement.width + columnDelta, minimumWidth, maximum),
    };
  }
  const right = placement.x + placement.width;
  const minimumX = Math.max(0, right - maximumWidth);
  const maximumX = right - minimumWidth;
  const x = clamp(placement.x + columnDelta, minimumX, maximumX);
  return { x, width: right - x };
};

export const hasDashboardOverlap = (placements: DashboardRuntimePlacement[], gap = 0) =>
  placements.some((current, index) =>
    placements
      .slice(index + 1)
      .some(
        (candidate) =>
          current.left < candidate.left + candidate.width + gap &&
          current.left + current.width + gap > candidate.left &&
          current.top < candidate.top + candidate.height + gap &&
          current.top + current.height + gap > candidate.top
      )
  );

export const reorderPlacement = (
  placements: DashboardWidgetPlacement[],
  key: DashboardModuleKey,
  nextPlacement: DashboardWidgetPlacement,
  runtimePlacements: DashboardRuntimePlacement[],
  rowUnit = DASHBOARD_ROW_UNIT
) => {
  const runtimeByKey = new Map(runtimePlacements.map((placement) => [placement.key, placement]));
  const peers = placements
    .filter((placement) => placement.sectionId === nextPlacement.sectionId && placement.key !== key)
    .sort((a, b) => {
      const aRuntime = runtimeByKey.get(a.key);
      const bRuntime = runtimeByKey.get(b.key);
      const aTop = aRuntime?.top ?? a.preferredRow * rowUnit;
      const bTop = bRuntime?.top ?? b.preferredRow * rowUnit;
      const aLeft = aRuntime?.left ?? a.x;
      const bLeft = bRuntime?.left ?? b.x;
      return aTop - bTop || aLeft - bLeft || a.layoutOrder - b.layoutOrder || a.key.localeCompare(b.key);
    });
  const targetTop = nextPlacement.preferredRow * rowUnit;
  const insertionIndex = peers.findIndex((placement) => {
    const runtime = runtimeByKey.get(placement.key);
    const top = runtime?.top ?? placement.preferredRow * rowUnit;
    const left = runtime?.left ?? placement.x;
    return top > targetTop || (top === targetTop && left >= nextPlacement.x);
  });
  const ordered = [...peers];
  ordered.splice(insertionIndex === -1 ? ordered.length : insertionIndex, 0, nextPlacement);
  const orderByKey = new Map(ordered.map((placement, index) => [placement.key, index]));
  return placements.map((placement) => {
    if (placement.sectionId !== nextPlacement.sectionId && placement.key !== key) return placement;
    const source = placement.key === key ? nextPlacement : placement;
    return { ...source, layoutOrder: orderByKey.get(source.key) ?? source.layoutOrder };
  });
};

export const createDashboardEditorPreview = ({
  placements,
  runtimePlacements,
  measurements,
  containerWidth,
  key,
  nextPlacement,
  reorder = true,
  gap = DASHBOARD_GAP,
  rowUnit = DASHBOARD_ROW_UNIT,
}: {
  placements: DashboardWidgetPlacement[];
  runtimePlacements: DashboardRuntimePlacement[];
  measurements: Map<DashboardModuleKey, DashboardWidgetMeasurement>;
  containerWidth: number;
  key: DashboardModuleKey;
  nextPlacement: DashboardWidgetPlacement;
  reorder?: boolean;
  gap?: number;
  rowUnit?: number;
}) => {
  const current = placements.map((placement) => ({ ...placement }));
  const source = current.find((placement) => placement.key === key);
  if (!source) {
    return {
      placements: current,
      layout: solveDashboardSection({ placements: current, measurements, containerWidth, gap, rowUnit }),
    };
  }
  const candidate = {
    ...source,
    ...nextPlacement,
    preferredRow: clamp(nextPlacement.preferredRow, 0, DASHBOARD_MAX_PREFERRED_ROW),
  };
  const updated = reorder
    ? reorderPlacement(current, key, candidate, runtimePlacements, rowUnit)
    : current.map((placement) => (placement.key === key ? candidate : placement));
  const layout = solveDashboardSection({ placements: updated, measurements, containerWidth, gap, rowUnit });
  return {
    placements: updated,
    layout,
  };
};
